/**
 * Cloud File Picker Component
 * Modal for browsing and selecting files from Google Drive or Dropbox.
 * Remembers the last browsed location per provider.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { activeLocale } from '@/utils/locale';
import { Spin } from 'antd';
import { useLanguage } from '../../../hooks/useLanguage';
import { CloudStorageApi } from '../services/cloudStorageService';
import { useCloudConnections } from '../hooks/useCloudConnections';
import { GoogleAccessPrompt } from '@/modules/cloud-storage/components/GoogleAccessPrompt';
import { useGoogleReadGate } from '@/modules/cloud-storage/hooks/useGoogleReadGate';
import type { CloudFolder, CloudFile } from '../types';
import './CloudFilePicker.css';

interface CloudFilePickerProps {
  visible: boolean;
  onSelect: (provider: string, fileIds: string[]) => void;
  onCancel: () => void;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

const PROVIDERS = [
  { key: 'google_drive', label: 'Google Drive' },
  { key: 'dropbox', label: 'Dropbox' },
];

export const CloudFilePicker: React.FC<CloudFilePickerProps> = ({
  visible,
  onSelect,
  onCancel,
}) => {
  const { t } = useLanguage();
  const { connections, loadConnections, isConnected } = useCloudConnections();
  const [activeProvider, setActiveProvider] = useState<string>('');
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  // Google Drive lists the person's own folders only with Google's `read`
  // level (drive.readonly), asked for here the first time it is needed.
  const gate = useGoogleReadGate(visible && activeProvider === 'google_drive');
  const { pending: accessPending, needsRead } = gate;
  const defaultAppliedFor = useRef<string>('');

  useEffect(() => {
    if (visible) {
      loadConnections();
      setSelectedFileIds([]);
      setBreadcrumbs([{ id: null, name: 'Root' }]);
      setCurrentFolderId(null);
      setFolders([]);
      setFiles([]);
      setActiveProvider('');
      defaultAppliedFor.current = '';
    }
  }, [visible, loadConnections]);

  // Auto-select first connected provider
  useEffect(() => {
    if (visible && connections.length > 0 && !activeProvider) {
      const firstConnected = PROVIDERS.find((p) => isConnected(p.key));
      if (firstConnected) {
        setActiveProvider(firstConnected.key);
      }
    }
  }, [visible, connections, activeProvider, isConnected]);

  // Load saved default folder when provider activates
  useEffect(() => {
    if (!activeProvider || !visible || accessPending || needsRead) return;
    if (defaultAppliedFor.current === activeProvider) return;
    defaultAppliedFor.current = activeProvider;

    CloudStorageApi.getDefaultExportFolder(activeProvider)
      .then((saved) => {
        if (saved && saved.breadcrumbs?.length) {
          setBreadcrumbs(saved.breadcrumbs);
          setCurrentFolderId(saved.folder_id || null);
        } else {
          setBreadcrumbs([{ id: null, name: 'Root' }]);
          setCurrentFolderId(null);
        }
      })
      .catch(() => {
        setBreadcrumbs([{ id: null, name: 'Root' }]);
        setCurrentFolderId(null);
      });
  }, [activeProvider, visible, accessPending, needsRead]);

  // Load folder contents when provider or folder changes
  useEffect(() => {
    if (activeProvider && visible && !accessPending && !needsRead) {
      loadFolderContents(activeProvider, currentFolderId);
    }
  }, [activeProvider, currentFolderId, visible, accessPending, needsRead]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFolderContents = useCallback(
    async (provider: string, folderId: string | null) => {
      setLoadingContent(true);
      try {
        const [folderList, fileList] = await Promise.all([
          CloudStorageApi.listFolders(provider, folderId || undefined),
          CloudStorageApi.listFiles(provider, folderId || undefined),
        ]);
        setFolders(folderList);
        setFiles(fileList);
      } catch (error) {
        if (!gate.refusedBy(error)) console.error('Error loading cloud contents:', error);
        setFolders([]);
        setFiles([]);
      } finally {
        setLoadingContent(false);
      }
    },
    []
  );

  const saveCurrentPosition = (provider: string) => {
    CloudStorageApi.setDefaultExportFolder(
      provider,
      currentFolderId || '',
      breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 1].name : 'Root',
      breadcrumbs,
    ).catch(() => {});
  };

  const handleProviderChange = (providerKey: string) => {
    setActiveProvider(providerKey);
    setSelectedFileIds([]);
    defaultAppliedFor.current = '';
  };

  const handleFolderClick = (folder: CloudFolder) => {
    setCurrentFolderId(folder.folder_id);
    setBreadcrumbs((prev) => [...prev, { id: folder.folder_id, name: folder.name }]);
    setSelectedFileIds([]);
  };

  const handleBreadcrumbClick = (index: number) => {
    const item = breadcrumbs[index];
    setCurrentFolderId(item.id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setSelectedFileIds([]);
  };

  const handleFileToggle = (fileId: string) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFileIds.length === files.length) {
      setSelectedFileIds([]);
    } else {
      setSelectedFileIds(files.map((f) => f.file_id));
    }
  };

  const handleConfirm = () => {
    if (selectedFileIds.length > 0) {
      saveCurrentPosition(activeProvider);
      onSelect(activeProvider, selectedFileIds);
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!visible) return null;

  const connectedProviders = PROVIDERS.filter((p) => isConnected(p.key));

  return (
    <div className="cloud-picker-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="cloud-picker-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cloud-picker-header">
          <div>
            <h2 className="cloud-picker-title">{t('cloudStorage.addFromCloud')}</h2>
            <p className="cloud-picker-subtitle">{t('cloudStorage.addFromCloudSubtitle')}</p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onCancel}
            aria-label={t('common.close')}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Provider Tabs */}
        {connectedProviders.length > 0 ? (
          <>
            <div className="cloud-picker-tabs">
              {connectedProviders.map((provider) => (
                <button
                  key={provider.key}
                  type="button"
                  className={`cloud-picker-tab ${activeProvider === provider.key ? 'active' : ''}`}
                  onClick={() => handleProviderChange(provider.key)}
                >
                  {provider.label}
                </button>
              ))}
            </div>

            {/* Breadcrumbs */}
            <div className="cloud-picker-breadcrumbs">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className="breadcrumb-separator">/</span>}
                  <button
                    type="button"
                    className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                    onClick={() => handleBreadcrumbClick(index)}
                  >
                    {crumb.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Content Area */}
            <div className="cloud-picker-content">
              {needsRead ? (
                <GoogleAccessPrompt
                  access="read"
                  reason="browse"
                  variant="card"
                  onGranted={gate.reset}
                />
              ) : loadingContent || accessPending ? (
                <div className="cloud-picker-loading">
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  {/* Folders */}
                  {folders.map((folder) => (
                    <div
                      key={folder.folder_id}
                      className="cloud-picker-item cloud-picker-folder"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <div className="cloud-picker-item-icon folder-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <span className="cloud-picker-item-name" dir="auto">{folder.name}</span>
                      <svg className="cloud-picker-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  ))}

                  {/* Select All (when files exist) */}
                  {files.length > 0 && (
                    <div className="cloud-picker-select-all">
                      <label className="cloud-picker-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedFileIds.length === files.length && files.length > 0}
                          onChange={handleSelectAll}
                          className="cloud-picker-checkbox"
                        />
                        {t('cloudStorage.selectAllFiles', { count: files.length })}
                      </label>
                    </div>
                  )}

                  {/* Files */}
                  {files.map((file) => (
                    <div
                      key={file.file_id}
                      className={`cloud-picker-item cloud-picker-file ${selectedFileIds.includes(file.file_id) ? 'selected' : ''}`}
                      onClick={() => handleFileToggle(file.file_id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFileIds.includes(file.file_id)}
                        onChange={() => handleFileToggle(file.file_id)}
                        className="cloud-picker-checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="cloud-picker-item-icon file-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div className="cloud-picker-file-info">
                        <span className="cloud-picker-item-name" dir="auto">{file.name}</span>
                        <span className="cloud-picker-file-meta">
                          {file.size != null && formatFileSize(file.size)}
                          {file.modified_time && (
                            <span className="cloud-picker-file-date">
                              {new Date(file.modified_time).toLocaleDateString(activeLocale())}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Empty State */}
                  {folders.length === 0 && files.length === 0 && (
                    <div className="cloud-picker-empty">
                      <p>{t('cloudStorage.folderEmpty')}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="cloud-picker-footer">
              <span className="cloud-picker-selection-count">
                {t('cloudStorage.filesSelected', { count: selectedFileIds.length })}
              </span>
              <div className="cloud-picker-footer-actions">
                <button type="button" className="btn-cancel" onClick={onCancel}>
                  {t('vault.cancel')}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConfirm}
                  disabled={selectedFileIds.length === 0}
                >
                  {t('cloudStorage.addSelected')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="cloud-picker-no-connections">
            <div className="cloud-picker-no-connections-icon">
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
              </svg>
            </div>
            <h3>{t('cloudStorage.noCloudConnected')}</h3>
            <p>{t('cloudStorage.connectToBrowseFiles')}</p>
            <button type="button" className="btn-cancel" onClick={onCancel}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
